import {Controller} from '@hotwired/stimulus';
import {createHTMLElementFromTemplate} from 'app/utils/dom';
import {throttle} from "throttle-debounce";
import {Typed} from "app/stimulus/typed-stimulus";
import {AsStimulusController} from "app/stimulus/as-stimulus-controller";
import {onLiveComponentRender} from "app/stimulus/live-component-render";
import DropdownController from "components/Dropdown/Dropdown/dropdown-controller";

type InlineStyle = {
    element: HTMLElement,
    property: string,
    value: string,
    priority: string,
};

/**
 * Collapses the buttons that do not fit on one row into the ⋮ dropdown. The menu holds copies of the collapsed
 * buttons; a click on a copy is forwarded to the real (hidden) button, so its Live action, link or modal opener
 * runs exactly as from the visible button. While a collapsed button is loading, the ⋮ trigger shows its loader.
 */
@AsStimulusController('responsive-button-group')
export default class extends Typed(
    Controller<HTMLElement>, {
        targets: {
            dropdown: HTMLElement,
            button: HTMLElement,
            buttonTemplate: HTMLTemplateElement,
        },
        classes: [
            'hasCollapsedItems',
            'hasOneVisibleItem',
            'loading',
            'opacityLoading',
        ] as const,
    }
)
{
    private containerResizeObserver!: ResizeObserver;
    private containerMutationObserver!: MutationObserver;
    private buttonMutationObserver!: MutationObserver;
    private removeLiveRenderListener: () => void = (): void => {};
    private observedWidth: number | null = null;
    private menuItemButtons = new Map<HTMLElement, HTMLElement>();
    private readonly forwardingPanels = new WeakSet<HTMLElement>();

    public initialize(): void {
        this.resize = throttle(100, this.resize.bind(this));
        this.containerResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]): void => {
            // Only width matters; the height changes with every measurement and would loop.
            const width = entries[entries.length - 1].contentRect.width;
            if (width === this.observedWidth) {
                return;
            }
            this.observedWidth = width;
            this.resize();
        });
        this.containerMutationObserver = new MutationObserver((): void => this.resize());
        this.buttonMutationObserver = new MutationObserver((): void => this.syncDropdownLoading());
    }

    public connect(): void {
        this.observedWidth = null;
        this.containerResizeObserver.observe(this.element);
        this.containerMutationObserver.observe(this.element, {childList: true});
        // Live re-applies its own state of the buttons on every render and may change their content.
        this.removeLiveRenderListener = onLiveComponentRender(this.element, (): void => {
            this.resize();
            this.syncDropdownLoading();
        });
        // Button widths change once the web font is loaded, without the container width changing.
        void document.fonts?.ready.then((): void => {
            if (this.element.isConnected) {
                this.resize();
            }
        });
        this.resize();
    }

    public disconnect(): void {
        this.containerResizeObserver.disconnect();
        this.containerMutationObserver.disconnect();
        this.buttonMutationObserver.disconnect();
        this.removeLiveRenderListener();
        this.removeLiveRenderListener = (): void => {};
        this.menuItemButtons = new Map();
    }

    public buttonTargetConnected(button: HTMLElement): void {
        this.buttonMutationObserver.observe(button, {attributes: true, attributeFilter: ['class']});
    }

    public resize(): void {
        if (!this.hasDropdownTarget) {
            return;
        }
        const buttonsToHide = this.getButtonsToHide();
        for (const button of this.buttonTargets) {
            buttonsToHide.includes(button)
                ? button.style.setProperty('display', 'none')
                : button.style.removeProperty('display');
        }
        buttonsToHide.length > 0
            ? this.dropdownTarget.style.removeProperty('display')
            : this.dropdownTarget.style.setProperty('display', 'none');
        this.element.classList.toggle(this.hasCollapsedItemsClass, buttonsToHide.length > 0);
        this.element.classList.toggle(this.hasOneVisibleItemClass, this.getVisibleButtons().length === 1);
        this.syncDropdownLoading();
    }

    public setupCollapsedItemsDropdown(event: CustomEvent<{ dropdown: HTMLElement }>): void {
        const dropdown = event.detail?.dropdown;
        if (!(dropdown instanceof HTMLElement)) {
            return;
        }
        const dropdownList = dropdown.querySelector<HTMLElement>('[data-responsive-button-group-dropdown-list]');
        if (dropdownList === null) {
            return;
        }
        dropdownList.replaceChildren();
        this.menuItemButtons = new Map();
        for (const button of this.getHiddenButtons()) {
            const buttonTemplate = this.buttonTemplateTargets.find((template: HTMLTemplateElement): boolean => button.contains(template));
            if (buttonTemplate === undefined) {
                continue;
            }
            const menuItem = createHTMLElementFromTemplate(buttonTemplate);
            if (button.hasAttribute('disabled')) {
                menuItem.setAttribute('disabled', '');
            }
            this.menuItemButtons.set(menuItem, button);
            dropdownList.appendChild(menuItem);
        }
        if (!this.forwardingPanels.has(dropdown)) {
            this.forwardingPanels.add(dropdown);
            // Captured on the panel so the copy's own default action never runs; the real button is clicked instead.
            dropdown.addEventListener('click', (clickEvent: MouseEvent): void => this.forwardClick(clickEvent), {capture: true});
        }
    }

    private forwardClick(event: MouseEvent): void {
        if (!(event.target instanceof Node)) {
            return;
        }
        for (const [menuItem, button] of this.menuItemButtons) {
            if (!menuItem.contains(event.target)) {
                continue;
            }
            // A modified click on a link copy opens the link natively (new tab or window).
            if (menuItem instanceof HTMLAnchorElement && (event.ctrlKey || event.metaKey || event.shiftKey)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            // The menu copy disappears with the panel; the focus stays on the ⋮ trigger, unless the action
            // of the real button moves it (e.g. into a modal).
            this.dropdownTarget.focus({preventScroll: true});
            void DropdownController.get(this.dropdownTarget)?.close();
            button.click();
            return;
        }
    }

    /**
     * A collapsed button is hidden, so its loader is shown on the ⋮ trigger instead.
     */
    private syncDropdownLoading(): void {
        if (!this.hasDropdownTarget) {
            return;
        }
        const hiddenButtons = this.getHiddenButtons();
        const isLoading = hiddenButtons.some((button: HTMLElement): boolean => button.classList.contains(this.loadingClass));
        const isOpacityLoading = hiddenButtons.some((button: HTMLElement): boolean => button.classList.contains(this.opacityLoadingClass));
        this.dropdownTarget.classList.toggle(this.loadingClass, isLoading);
        this.dropdownTarget.classList.toggle(this.opacityLoadingClass, isOpacityLoading && !isLoading);
    }

    private getVisibleButtons(): HTMLElement[] {
        return this.buttonTargets.filter((button: HTMLElement): boolean => button.style.display !== 'none');
    }

    private getHiddenButtons(): HTMLElement[] {
        return this.buttonTargets.filter((button: HTMLElement): boolean => button.style.display === 'none');
    }

    /**
     * One layout pass: let the buttons wrap with their natural width and the ⋮ trigger placed first; every button
     * that lands below the trigger's row does not fit and is collapsed. At least one button always stays visible.
     */
    private getButtonsToHide(): HTMLElement[] {
        if (this.buttonTargets.length <= 1) {
            return [];
        }
        // The caller's inline styles are restored after the measurement.
        const overridden: InlineStyle[] = [];
        this.overrideStyle(overridden, this.element, 'display', 'flex');
        this.overrideStyle(overridden, this.element, 'flex-direction', 'row');
        this.overrideStyle(overridden, this.element, 'flex-wrap', 'wrap');
        this.overrideStyle(overridden, this.dropdownTarget, 'display', 'none');
        this.overrideStyle(overridden, this.dropdownTarget, 'order', '-1');
        for (const button of this.buttonTargets) {
            button.style.removeProperty('display');
            this.overrideStyle(overridden, button, 'flex-grow', '0');
            this.overrideStyle(overridden, button, 'flex-shrink', '0');
            this.overrideStyle(overridden, button, 'white-space', 'nowrap');
        }
        try {
            const firstTop = this.buttonTargets[0].getBoundingClientRect().top;
            const allFit = this.buttonTargets.every((button: HTMLElement): boolean => button.getBoundingClientRect().top === firstTop);
            if (allFit) {
                return [];
            }
            this.dropdownTarget.style.removeProperty('display');
            const baseTop = this.dropdownTarget.getBoundingClientRect().top;
            const toHide = this.buttonTargets.filter((button: HTMLElement): boolean => button.getBoundingClientRect().top > baseTop);
            if (toHide.length === this.buttonTargets.length) {
                toHide.shift();
            }
            return toHide;
        } finally {
            for (const style of overridden.reverse()) {
                if (style.value === '') {
                    style.element.style.removeProperty(style.property);
                } else {
                    style.element.style.setProperty(style.property, style.value, style.priority);
                }
            }
        }
    }

    private overrideStyle(overridden: InlineStyle[], element: HTMLElement, property: string, value: string): void {
        overridden.push({
            element: element,
            property: property,
            value: element.style.getPropertyValue(property),
            priority: element.style.getPropertyPriority(property),
        });
        element.style.setProperty(property, value, 'important');
    }
}
